import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';

import { parseDecimal } from 'utils/parser.utils';

import Web3Singleton from '../web3Singleton';
import { useRMVCurrency, useUSDCCurrency } from './useCurrency';

const balanceCache = new Map<string, string>();

export const clearBalanceCache = () => {
  balanceCache.clear();
};

const getBalanceCacheKey = (walletAddress?: string, currencyAddress?: string): string | null => {
  if (!walletAddress || !currencyAddress) {
    return null;
  }

  return `${walletAddress.toLowerCase()}:${currencyAddress.toLowerCase()}`;
};

export const useBalanceState = (currencyId?: number) => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.id === currencyId);
  const cacheKey = getBalanceCacheKey(walletAddress, currency?.address);
  const [balance, setBalance] = useState(() => (cacheKey ? balanceCache.get(cacheKey) ?? '0' : '0'));
  const [loading, setLoading] = useState(!!currencies?.length);
  const lgoWeb3 = Web3Singleton.getInstance();

  useEffect(() => {
    if (!currencies?.length || !currency || !walletAddress || !cacheKey) {
      setBalance('0');

      return;
    }

    const cachedBalance = balanceCache.get(cacheKey);
    if (cachedBalance) {
      setBalance(cachedBalance);
    }

    fetchCurrenciesBalance();
  }, [cacheKey, currency, currencies, walletAddress, lgoWeb3, setBalance]);

  const fetchCurrenciesBalance = async () => {
    if (!currency || !cacheKey) {
      return;
    }

    try {
      const balance = await lgoWeb3.getCurrencyBalance(currency.address, walletAddress);
      balanceCache.set(cacheKey, balance);
      setBalance(balance);
    } catch {
      // Web3 RPC call failed
    } finally {
      setLoading(false);
    }
  };

  const refreshBalance = () => {
    fetchCurrenciesBalance();
  };
  const currencyBalance = currency && balance ? parseDecimal(balance, currency.decimal) : '0';

  return { loading, refreshBalance, balance: currencyBalance };
};

export const useRMVBalanceState = () => {
  const rmvCurrency = useRMVCurrency();
  const { loading, balance, refreshBalance } = useBalanceState(rmvCurrency?.id);

  return { loading, balance, refreshBalance };
};

export const useUSDCBalanceState = () => {
  const usdcCurrency = useUSDCCurrency();
  const { loading, balance, refreshBalance } = useBalanceState(usdcCurrency?.id);

  return { loading, balance, refreshBalance };
};
